import { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Settings } from 'react-feather';
import type { RemirrorEventListener } from 'remirror';
import styled from '@emotion/styled';
import type { Remirror as RemirrorTypes, QuickChartData } from '@trinity/chat';
import { setInsightsDataUtil } from '@trinity/chat';
import type { QuantFileTableData } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import type { MultipleBarChart } from '@trinity/chat/types/chat.chart';
import { NIVO_CHART_TYPES } from '../../utils/chart.js';
import { BasicRichText } from '../BasicRichText/BasicRichText.js';
import { ChartKeyEditModal } from '../ChartTheming';
import { LegendCollapseToggleContext } from '../Chat/context.js';
import { Lightbulb } from '../Chat.Widget/Lightbulb.js';
import { Modal } from '../Modal/Modal.js';
import type { BasicMarkupEditorExtensions } from '../Remirror/BasicMarkupEditor.js';
import { BasicMarkupEditor } from '../Remirror/BasicMarkupEditor.js';
import { CompactChartLegend } from '../Widgets/Charts/CompactLegend.js';
import { ChartLegend } from '../Widgets/Charts/Legend.js';
import { KPIWidget } from '../Widgets/KPIWidget.js';
import { nivoOptionsFromChart, renderNivoChart } from '../Widgets/nivo-chart.js';
import { ProgressWidgetChart } from '../Widgets/ProgressWidgetChart.js';
import { AnalysisTable } from './Analysis.Table.js';

type LegendEntry = { key: string; color: string; originalKey: string };

const collectSeriesKeys = (multipleBarChart: MultipleBarChart): string[] => {
  const keys = new Set<string>();
  for (const series of multipleBarChart.data) {
    const points = Array.isArray(series?.data) ? series.data : [];
    for (const point of points) {
      if (point) keys.add(point.x);
    }
  }
  return Array.from(keys);
};

/** The data keys that get a legend entry, in series order. Data tables and widgets have no legend. */
const getLegendKeys = (chart: QuickChartData): string[] => {
  switch (chart.type) {
    case 'bar':
    case 'stacked-bar':
      return chart.chart.data.map((item) => item.x);
    case 'pie':
      return chart.chart.data.map((item) => item.label);
    case 'multiple-clustered-bar':
    case 'multiple-stacked-bar':
      return collectSeriesKeys(chart.chart);
    default:
      return [];
  }
};

const buildLegend = (
  chart: QuickChartData,
  colors: string[],
  colorOverrides: Record<string, string> | null,
  labelOverrides: Record<string, string> | null
): LegendEntry[] =>
  getLegendKeys(chart).map((originalKey, i) => ({
    key: labelOverrides?.[originalKey] ?? originalKey,
    color: colorOverrides?.[originalKey] ?? colors[i % colors.length],
    originalKey,
  }));

export type ChartVisualizationProps = {
  chart: QuickChartData;
  hideLegend?: boolean;
  legendOpenDefault?: boolean;
  /** Resolved theme palette; the app owns how it is looked up (it depends on app-level branding contexts). */
  colors: string[];
  colorOverrides?: Record<string, string> | null;
  labelOverrides?: Record<string, string> | null;
  onColorOverridesChange?: (overrides: Record<string, string>) => void;
  onLabelOverridesChange?: (overrides: Record<string, string>) => void;
  onInsightsChange?: (insights: string | undefined) => void;
  onResetOverrides?: () => void;
  useCompactLegend?: boolean;
  isInteractive?: boolean;
  decimalPlaces?: number;
};

export type ChartVisualizationRef = {
  getImageExportRef: () => HTMLTableElement | null;
};

export const ChartVisualization = forwardRef<ChartVisualizationRef, ChartVisualizationProps>(
  (
    {
      chart,
      legendOpenDefault = false,
      colors,
      colorOverrides = null,
      labelOverrides = null,
      onColorOverridesChange,
      onLabelOverridesChange,
      onInsightsChange,
      onResetOverrides,
      hideLegend = false,
      useCompactLegend = false,
      isInteractive = true,
      decimalPlaces,
    },
    ref
  ) => {
    const [legendOpen, setLegendOpen] = useState(legendOpenDefault);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const imageExportRef = useRef<HTMLTableElement>(null);

    // Insights editor state (Remirror)
    type RootNode = RemirrorTypes.RootNode;
    const [insightsContent, setInsightsContent] = useState<RootNode | null>(() => {
      return setInsightsDataUtil(chart);
    });

    // Reset editor content when the chart changes (e.g., user switches to a different chart)
    useEffect(() => {
      setInsightsContent(setInsightsDataUtil(chart));
    }, [chart]);

    const handleInsightsChange: RemirrorEventListener<BasicMarkupEditorExtensions> = useCallback((params) => {
      if (!params.tr?.docChanged) return;
      const newValue = params.state.doc.toJSON() as RootNode;
      setInsightsContent(newValue);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getImageExportRef: () => imageExportRef.current,
      }),
      []
    );

    // Editing the key only makes sense when the caller handles the result
    const canEditChartKey = Boolean(onColorOverridesChange ?? onLabelOverridesChange ?? onInsightsChange);

    const legend = useMemo(
      () => buildLegend(chart, colors, colorOverrides, labelOverrides),
      [chart, colors, colorOverrides, labelOverrides]
    );

    const handleSaveOverrides = useCallback(
      (newColorOverrides: Record<string, string>, newLabelOverrides: Record<string, string>) => {
        onColorOverridesChange?.(newColorOverrides);
        onLabelOverridesChange?.(newLabelOverrides);

        // Serialize insights from Remirror state
        let serializedInsights: string | undefined;
        if (insightsContent && insightsContent.content && insightsContent.content.length > 0) {
          const hasContent = insightsContent.content.some((node) => node.content && node.content.length > 0);
          if (hasContent) {
            serializedInsights = JSON.stringify(insightsContent);
          }
        }
        onInsightsChange?.(serializedInsights);
      },
      [onColorOverridesChange, onLabelOverridesChange, onInsightsChange, insightsContent]
    );

    const renderedInsights = useMemo(() => {
      if (!chart.insights) return null;

      try {
        const parsedInsights = JSON.parse(chart.insights) as RootNode;
        return <BasicRichText value={parsedInsights} />;
      } catch {
        return null;
      }
    }, [chart.insights]);

    const insightsSection = useMemo(
      () => (
        <InsightsSectionWrap>
          <InsightsLabel>Add Insights (Optional)</InsightsLabel>
          <InsightsEditorWrap>
            <BasicMarkupEditor content={insightsContent} onChange={handleInsightsChange} />
          </InsightsEditorWrap>
          <InsightsHint>Add context, analysis, or additional information about this chart.</InsightsHint>
        </InsightsSectionWrap>
      ),
      [insightsContent, handleInsightsChange]
    );

    return (
      <div style={{ width: '100%' }}>
        <LegendCollapseToggleContext.Provider value={[legendOpen, setLegendOpen]}>
          <ChartContainer>
            <ChartContent>
              {NIVO_CHART_TYPES.has(chart.type) ? (
                <NivoChartContainer>{renderChart()}</NivoChartContainer>
              ) : (
                renderChart()
              )}
            </ChartContent>
            {chart.type !== 'kpi-widget' && chart.insights && (
              <InsightsContainer style={{ marginTop: 15, marginBottom: 8 }}>
                <BarInsights>
                  <Lightbulb fill={'#4c4c4c'} size={12} />
                  {renderedInsights}
                </BarInsights>
              </InsightsContainer>
            )}
            {legend.length > 0 && !hideLegend && !useCompactLegend && (
              <LegendContainer>
                <ChartLegend legend={legend} />
                {canEditChartKey && (
                  <EditButton onClick={() => setIsEditModalOpen(true)} title="Edit Chart Key">
                    <Settings size={16} />
                  </EditButton>
                )}
              </LegendContainer>
            )}
            {legend.length > 0 && !hideLegend && useCompactLegend && <CompactChartLegend legend={legend} />}
          </ChartContainer>
          <ChartKeyEditModal
            open={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            legendData={legend}
            colorOverrides={colorOverrides}
            labelOverrides={labelOverrides}
            insightsSection={insightsSection}
            onSave={handleSaveOverrides}
            onReset={onResetOverrides}
            ModalComponent={Modal}
          />
        </LegendCollapseToggleContext.Provider>
      </div>
    );

    function renderChart() {
      switch (chart.type) {
        case 'bar':
        case 'stacked-bar': {
          // Apply label overrides to chart data
          const transformedData = chart.chart.data.map((item) => ({
            ...item,
            x: labelOverrides?.[item.x] ?? item.x,
          }));
          const chartColors = chart.chart.data.map((item, i) => {
            const originalKey = item.x;
            return colorOverrides?.[originalKey] ?? colors[i % colors.length];
          });
          const transformedChart = {
            ...chart.chart,
            data: transformedData,
          };

          return renderNivoChart({ ...chart, chart: transformedChart }, chartColors, {
            ...nivoOptionsFromChart(chart),
            animate: true,
            columnN: chart.options?.n,
            isInteractive,
          });
        }

        case 'pie': {
          // Apply label overrides to chart data
          const transformedData = chart.chart.data.map((item) => ({
            ...item,
            label: labelOverrides?.[item.label] ?? item.label,
          }));
          const chartColors = chart.chart.data.map((item, i) => {
            const originalKey = item.label;
            return colorOverrides?.[originalKey] ?? colors[i % colors.length];
          });
          const transformedChart = {
            ...chart.chart,
            data: transformedData,
          };
          return renderNivoChart({ ...chart, chart: transformedChart }, chartColors, {
            ...nivoOptionsFromChart(chart),
            animate: true,
            columnN: chart.options?.n,
            isInteractive,
          });
        }

        case 'multiple-clustered-bar':
        case 'multiple-stacked-bar': {
          // Validate chart data structure exists
          if (!chart.chart.data || !Array.isArray(chart.chart.data) || chart.chart.data.length === 0) {
            return (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                Unable to render chart: Invalid data structure
              </div>
            );
          }

          // Apply label overrides to chart data
          const transformedData = chart.chart.data.map((series) => ({
            ...series,
            data: series.data.map((point) => ({
              ...point,
              x: labelOverrides?.[point.x] ?? point.x,
            })),
          }));

          const allKeys = new Set<string>();
          chart.chart.data.forEach((series) => {
            // Defensive check: ensure series has valid data array
            if (series && series.data && Array.isArray(series.data)) {
              series.data.forEach((d) => allKeys.add(d.x));
            }
          });
          const keys = Array.from(allKeys);
          const chartColors = keys.map((key, i) => {
            return colorOverrides?.[key] ?? colors[i % colors.length];
          });

          const transformedChart = {
            ...chart.chart,
            data: transformedData,
          };

          return renderNivoChart({ ...chart, chart: transformedChart }, chartColors, {
            ...nivoOptionsFromChart(chart),
            animate: chart.type === 'multiple-clustered-bar',
            isInteractive,
          });
        }

        case 'data-table': {
          // For thumbnails (non-interactive), show a simple placeholder
          // since AnalysisTable uses theme extensions not available in all contexts
          if (!isInteractive) {
            return (
              <DataTableContainer>
                <div
                  style={{
                    height: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    color: '#666',
                    fontSize: 14,
                  }}
                >
                  Data Table
                </div>
              </DataTableContainer>
            );
          }

          // Create table data with custom labels if provided
          const tableData: QuantFileTableData = {
            columnNames: chart.customColumnNames || chart.data.columnNames,
            rowNames: chart.customRowNames || chart.data.rowNames,
            records: chart.data.records,
            metadata: chart.data.metadata,
          };

          return (
            <DataTableContainer>
              <AnalysisTable
                ref={imageExportRef}
                data={tableData}
                headerBackgroundColor={chart.headerColor}
                initialSortConfig={chart.sortConfig}
                disableSorting={true}
                disableSelection={true}
                decimalPlaces={decimalPlaces}
              />
            </DataTableContainer>
          );
        }

        case 'kpi-widget': {
          return (
            <KPIWidgetContainer>
              <KPIWidget
                ref={imageExportRef}
                title={chart.title}
                backgroundColor={chart.backgroundColor}
                values={chart.values}
                labels={chart.labels}
                insights={chart.insights}
              />
            </KPIWidgetContainer>
          );
        }

        case 'progress-bar-widget':
        case 'semi-circle-progress-widget': {
          return <ProgressWidgetChart ref={imageExportRef} chart={chart} themeColors={colors} />;
        }

        default:
          throw new Error(`Unsupported chart type`);
      }
    }
  }
);

ChartVisualization.displayName = 'ChartVisualization';

const ChartContainer = styled.div({
  position: 'relative',
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const LegendContainer = styled.div`
  position: relative;
`;

const EditButton = styled.button`
  position: absolute;
  top: 32px;
  transform: translateY(-50%);
  right: 40px;
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #666;

  &:hover {
    color: #333;
  }

  &:active {
    transform: translateY(calc(-50% + 1px));
  }
`;

const ChartContent = styled.div({
  display: 'flex',
  flex: 1,
  width: '100%',
});
// Lets the insights text scale down in narrow cards (e.g. Widget Library), like the KPI and progress widgets
const InsightsContainer = styled.div({
  containerType: 'inline-size',
  width: '100%',
});
const BarInsights = styled.div(() => ({
  display: 'grid',
  gap: '8px',
  gridTemplateColumns: 'auto 1fr',
  '@container (max-width: 400px)': {
    fontSize: '0.80em',
    p: { fontSize: '0.80em' },
    strong: { fontSize: '0.80em' },
    em: { fontSize: '0.80em' },
    'strong em': { fontSize: '0.80em' },
    '> svg': { width: '10px' },
  },
}));
const DataTableContainer = styled.div({
  width: '100%',
  overflow: 'hidden',
});

const NivoChartContainer = styled.div({
  height: 320,
  width: '100%',
});

const KPIWidgetContainer = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',
  minWidth: 0, // Allow flex item to shrink below its minimum content size
  '& > *': {
    maxWidth: '100%',
    minWidth: 0,
    flex: '0 1 auto', // Allow shrinking
  },
});

const InsightsSectionWrap = styled.div({
  marginBottom: '24px',
});

const InsightsLabel = styled.div(({ theme }) => ({
  fontSize: 18,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.text.primary,
}));

const InsightsEditorWrap = styled.div({
  minHeight: '120px',
  marginTop: '8px',
});

const InsightsHint = styled.div({
  fontSize: '12px',
  color: '#666',
  marginTop: '8px',
  fontStyle: 'italic',
});
